// components/Auth/Register.jsx
import React, { useState, useEffect, Suspense, lazy } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus,
  Mail,
  User,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Loader2,
  ChevronLeft,
  ArrowRight,
  Building2,
  Shield,
  AlertTriangle,
  Briefcase,
  GraduationCap,
  Newspaper,
} from "lucide-react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
} from "@react-three/drei";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

// Précharger le modèle 3D
useGLTF.preload("/models/2014_lamborghini_veneno_roadster.glb");

// Composant du modèle 3D
function CarModel() {
  const { scene } = useGLTF("/models/2014_lamborghini_veneno_roadster.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

// Composant de la scène 3D
function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      style={{ width: "100%", height: "100%" }}
      dpr={[1, 1.5]}
      performance={{ min: 0.5 }}
    >
      <color attach="background" args={["#0f172a"]} />
      <Suspense fallback={null}>
        <Stage environment="city" intensity={0.5}>
          <CarModel />
        </Stage>
        <Environment preset="city" />
        <OrbitControls
          autoRotate
          autoRotateSpeed={0.5}
          enableZoom={false}
          enablePan={false}
        />
      </Suspense>
    </Canvas>
  );
}

// Composant de fallback pendant le chargement du modèle 3D
const LoadingFallback = () => (
  <div className="w-full h-full bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="text-center"
    >
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"
      />
      <p className="text-purple-300 text-sm">Chargement de la scène 3D...</p>
    </motion.div>
  </div>
);

// Fond animé simple comme alternative au modèle 3D
const AnimatedBackground = () => (
  <div className="w-full h-full bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-2 h-2 bg-purple-500/30 rounded-full"
        initial={{
          x: Math.random() * (typeof window !== "undefined" ? window.innerWidth : 1000),
          y: Math.random() * (typeof window !== "undefined" ? window.innerHeight : 800),
        }}
        animate={{
          x: Math.random() * (typeof window !== "undefined" ? window.innerWidth : 1000),
          y: Math.random() * (typeof window !== "undefined" ? window.innerHeight : 800),
        }}
        transition={{
          duration: Math.random() * 10 + 10,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut",
        }}
      />
    ))}
    <motion.div
      className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"
      animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3],
      }}
      transition={{
        duration: 5,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
    <motion.div
      className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-600/20 rounded-full blur-3xl"
      animate={{
        scale: [1.2, 1, 1.2],
        opacity: [0.4, 0.2, 0.4],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  </div>
);

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [show3DScene, setShow3DScene] = useState(false);
  const [use3DScene, setUse3DScene] = useState(true);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    genre: "formateur",
    companyName: "",
    domain: "Automobile",
    specialite: "mecanique",
    canEditActualites: false,
  });

  const specialiteOptions = {
    Automobile: ["mecanique", "carrosserie", "electricite"],
    Vente: ["Vente Supérieure", "Réception Après Vente", "GPME", "NDRC", "CCST"],
  };

  useEffect(() => {
    if (use3DScene) {
      const timer = setTimeout(() => {
        setShow3DScene(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [use3DScene]);

  const formatSpecialite = (spec) => {
    const specialiteLabels = {
      mecanique: "🔧 Mécanique",
      carrosserie: "🚗 Carrosserie",
      electricite: "⚡ Électricité",
      "Vente Supérieure": "💼 Vente Supérieure",
      "Réception Après Vente": "📋 Réception Après Vente",
      GPME: "🏢 GPME (Gestion de la PME)",
      NDRC: "💰 NDRC (Négociation et Digitalisation)",
      CCST: "🎯 CCST (Conseiller Commercial)",
    };
    return specialiteLabels[spec] || spec;
  };

  const formatGenre = (genre) => {
    const genreLabels = {
      formateur: "👨‍🏫 Formateur",
      formatrice: "👩‍🏫 Formatrice",
      vie_scolaire: "📚 Vie Scolaire",
    };
    return genreLabels[genre] || genre;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "domain") {
      const newDomain = value;
      const firstSpecialite = specialiteOptions[newDomain][0];
      setFormData((prev) => ({
        ...prev,
        domain: newDomain,
        specialite: firstSpecialite,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    setError("");
  };

  const validateStep1 = () => {
    if (!formData.email) {
      setError("Email requis");
      return false;
    }
    if (!formData.email.includes("@")) {
      setError("Email invalide");
      return false;
    }
    if (!formData.firstName) {
      setError("Prénom requis");
      return false;
    }
    if (!formData.lastName) {
      setError("Nom requis");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.password) {
      setError("Mot de passe requis");
      return false;
    }
    if (formData.password.length < 8) {
      setError("Le mot de passe doit faire au moins 8 caractères");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.companyName) {
      setError("Nom de l'entreprise requis");
      return false;
    }
    return true;
  };

  const nextStep = () => {
    setError("");
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;

    if (step < 4) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const userData = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        company_name: formData.companyName.trim(),
        genre: formData.genre,
        domain: formData.domain,
        specialite: formData.specialite,
        can_edit_actualites: formData.canEditActualites,
        role: formData.genre,
      };

      console.log("📝 Inscription:", userData);

      const result = await register(
        formData.email.trim(),
        formData.password,
        userData
      );

      if (result.success) {
        setSuccess("✅ Inscription réussie ! Redirection...");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(result.error || "Erreur lors de l'inscription");
      }
    } catch (err) {
      console.error("❌ Erreur inscription:", err);
      setError(err.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const length = formData.password.length;
    if (length === 0) return { level: 0, text: "", color: "" };
    if (length < 8) return { level: 1, text: "Trop court", color: "bg-red-500" };
    if (length < 12) return { level: 2, text: "Acceptable", color: "bg-yellow-500" };
    return { level: 3, text: "Fort", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 py-32 relative overflow-hidden">
      {/* Fond 3D ou animé */}
      <div className="fixed inset-0 z-0">
        {use3DScene && show3DScene ? (
          <Suspense fallback={<LoadingFallback />}>
            <CarScene />
          </Suspense>
        ) : use3DScene ? (
          <LoadingFallback />
        ) : (
          <AnimatedBackground />
        )}
      </div>

      {/* Overlay sombre */}
      <div className="fixed inset-0 bg-black/40 z-0" />

      <div className="w-full max-w-5xl relative z-10 flex gap-8">
        {/* Panneau d'avertissement */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="hidden lg:flex flex-col gap-4 flex-1"
        >
          <motion.div
            animate={{
              boxShadow: [
                "0 0 20px rgba(234, 179, 8, 0.3)",
                "0 0 40px rgba(234, 179, 8, 0.6)",
                "0 0 20px rgba(234, 179, 8, 0.3)",
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-lg border-2 border-yellow-500/50 rounded-2xl p-6"
          >
            <div className="flex items-start gap-4">
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <AlertTriangle className="w-12 h-12 text-yellow-400 flex-shrink-0" />
              </motion.div>
              <div>
                <h3 className="text-xl font-bold text-yellow-400 mb-2">
                  ⚠️ Important
                </h3>
                <p className="text-white text-sm leading-relaxed">
                  Cette inscription est{" "}
                  <span className="font-bold text-yellow-300">
                    réservée aux employés autorisés
                  </span>{" "}
                  du CFA. Toute tentative d'inscription non autorisée sera
                  détectée et signalée.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-slate-900/60 backdrop-blur-lg border border-slate-700/50 rounded-2xl p-6"
          >
            <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              Sécurité et confidentialité
            </h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span>Données chiffrées de bout en bout</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span>Authentification sécurisée</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span>Conformité RGPD assurée</span>
              </li>
            </ul>
          </motion.div>

          {/* Indicateur des étapes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-slate-900/60 backdrop-blur-lg border border-slate-700/50 rounded-2xl p-6"
          >
            <h3 className="text-lg font-bold text-white mb-4">Étapes</h3>
            <div className="space-y-3">
              {[
                { num: 1, label: "Informations personnelles" },
                { num: 2, label: "Mot de passe" },
                { num: 3, label: "Profil professionnel" },
                { num: 4, label: "Confirmation" },
              ].map((item) => (
                <div
                  key={item.num}
                  className={`flex items-center gap-3 transition-colors ${
                    step >= item.num ? "text-white" : "text-slate-500"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                      step > item.num
                        ? "bg-green-500 text-white"
                        : step === item.num
                        ? "bg-purple-600 text-white"
                        : "bg-slate-700 text-slate-400"
                    }`}
                  >
                    {step > item.num ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      item.num
                    )}
                  </div>
                  <span className="text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Formulaire */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex-1 w-full max-w-md mx-auto lg:mx-0"
        >
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="bg-gradient-to-br from-purple-600 to-purple-700 p-4 rounded-full mb-4 inline-block"
              >
                <UserPlus className="w-8 h-8 text-white" />
              </motion.div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Inscription
              </h2>
              <p className="text-slate-400 text-sm">
                Étape {step} sur 4 — Créez votre compte
              </p>

              {/* Barre de progression */}
              <div className="mt-4 flex gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden"
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: i <= step ? "100%" : "0%" }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-purple-600 to-purple-400"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Messages d'erreur/succès */}
            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 mb-4 flex items-center gap-2"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-200 text-sm">{error}</p>
                </motion.div>
              )}

              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-green-500/10 border border-green-500/50 rounded-lg p-3 mb-4 flex items-center gap-2"
                >
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <p className="text-green-200 text-sm">{success}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-6">
              <AnimatePresence mode="wait">
                {/* ÉTAPE 1 - Informations personnelles */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-xs font-medium text-white mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Adresse email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-sm transition-all"
                        placeholder="votre.email@cfa-montargis.com"
                        required
                        autoComplete="email"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-white mb-2">
                          <User className="w-4 h-4 inline mr-1" />
                          Prénom
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-sm transition-all"
                          placeholder="Jean"
                          required
                          autoComplete="given-name"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-white mb-2">
                          <User className="w-4 h-4 inline mr-1" />
                          Nom
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-sm transition-all"
                          placeholder="Dupont"
                          required
                          autoComplete="family-name"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ÉTAPE 2 - Mot de passe */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-xs font-medium text-white mb-2">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Mot de passe
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none pr-10 text-sm transition-all"
                          placeholder="••••••••"
                          required
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
                          aria-label={
                            showPassword
                              ? "Masquer le mot de passe"
                              : "Afficher le mot de passe"
                          }
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {/* Indicateur de force */}
                      {formData.password && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-2 space-y-1"
                        >
                          <div className="flex gap-1">
                            {[1, 2, 3].map((i) => (
                              <div
                                key={i}
                                className={`flex-1 h-1 rounded-full transition-colors ${
                                  passwordStrength.level >= i
                                    ? passwordStrength.color
                                    : "bg-slate-700"
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-slate-500">
                            {passwordStrength.text}
                          </p>
                        </motion.div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-white mb-2">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Confirmer le mot de passe
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className={`w-full bg-slate-800/50 border rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none pr-10 text-sm transition-all ${
                            formData.confirmPassword &&
                            formData.password !== formData.confirmPassword
                              ? "border-red-500"
                              : formData.confirmPassword &&
                                formData.password === formData.confirmPassword
                              ? "border-green-500"
                              : "border-slate-700"
                          }`}
                          placeholder="••••••••"
                          required
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
                          aria-label={
                            showConfirmPassword
                              ? "Masquer le mot de passe"
                              : "Afficher le mot de passe"
                          }
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      {formData.confirmPassword && (
                        <p
                          className={`text-xs mt-1 ${
                            formData.password === formData.confirmPassword
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          {formData.password === formData.confirmPassword
                            ? "✓ Les mots de passe correspondent"
                            : "✗ Les mots de passe ne correspondent pas"}
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* ÉTAPE 3 - Profil professionnel */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-xs font-medium text-white mb-2">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        Entreprise / Établissement
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-sm transition-all"
                        placeholder="CFA Montargis"
                        required
                        autoComplete="organization"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-white mb-2">
                        👤 Fonction
                      </label>
                      <select
                        name="genre"
                        value={formData.genre}
                        onChange={handleChange}
                        className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-sm transition-all cursor-pointer"
                      >
                        <option value="formateur">👨‍🏫 Formateur</option>
                        <option value="formatrice">👩‍🏫 Formatrice</option>
                        <option value="vie_scolaire">📚 Vie Scolaire</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-white mb-2">
                          <Briefcase className="w-4 h-4 inline mr-1" />
                          Domaine
                        </label>
                        <select
                          name="domain"
                          value={formData.domain}
                          onChange={handleChange}
                          className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-sm transition-all cursor-pointer"
                        >
                          <option value="Automobile">🚗 Automobile</option>
                          <option value="Vente">💼 Vente</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-white mb-2">
                          <GraduationCap className="w-4 h-4 inline mr-1" />
                          Spécialité
                        </label>
                        <select
                          name="specialite"
                          value={formData.specialite}
                          onChange={handleChange}
                          className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-sm transition-all cursor-pointer"
                        >
                          {specialiteOptions[formData.domain].map((spec) => (
                            <option key={spec} value={spec}>
                              {formatSpecialite(spec)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ÉTAPE 4 - Confirmation */}
                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >
                    {/* Option éditeur d'actualités */}
                    <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="canEditActualites"
                          name="canEditActualites"
                          checked={formData.canEditActualites}
                          onChange={handleChange}
                          className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-700 text-purple-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer"
                        />
                        <div>
                          <label
                            htmlFor="canEditActualites"
                            className="text-sm font-medium text-white cursor-pointer flex items-center gap-2"
                          >
                            <Newspaper className="w-5 h-5 text-purple-400" />
                            Demander les droits d'éditeur d'actualités
                          </label>
                          <p className="text-xs text-slate-400 mt-1">
                            Cette demande sera soumise aux administrateurs pour
                            validation.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Récapitulatif */}
                    <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-700/40 rounded-lg p-4">
                      <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400" />
                        Récapitulatif de votre inscription
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Nom complet</span>
                          <span className="text-white font-medium">
                            {formData.firstName} {formData.lastName}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Email</span>
                          <span className="text-white font-medium truncate ml-4">
                            {formData.email}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Entreprise</span>
                          <span className="text-white font-medium">
                            {formData.companyName}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Fonction</span>
                          <span className="text-white font-medium">
                            {formatGenre(formData.genre)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Domaine</span>
                          <span className="text-white font-medium">
                            {formData.domain === "Automobile" ? "🚗" : "💼"}{" "}
                            {formData.domain}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Spécialité</span>
                          <span className="text-white font-medium">
                            {formatSpecialite(formData.specialite)}
                          </span>
                        </div>
                        {formData.canEditActualites && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Éditeur</span>
                            <span className="text-purple-400 font-medium">
                              ✓ Demandé
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Conditions */}
                    <p className="text-xs text-slate-500 text-center">
                      En cliquant sur "S'inscrire", vous acceptez nos conditions
                      d'utilisation et notre politique de confidentialité.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Boutons de navigation */}
              <div className="flex gap-3 mt-8">
                {step > 1 && (
                  <motion.button
                    type="button"
                    onClick={prevStep}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors border border-slate-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Retour
                  </motion.button>
                )}

                {step < 4 ? (
                  <motion.button
                    type="button"
                    onClick={nextStep}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 text-sm transition-all shadow-lg shadow-purple-500/25"
                  >
                    Continuer
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                ) : (
                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed text-sm transition-all shadow-lg shadow-green-500/25"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Inscription en cours...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        S'inscrire
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </form>

            {/* Lien connexion */}
            <p className="text-center text-slate-400 mt-6 text-sm">
              Déjà inscrit ?{" "}
              <a
                href="/login"
                className="text-purple-400 hover:text-purple-300 font-medium transition-colors hover:underline"
              >
                Se connecter
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;